import React from "react";
import { API } from "../API";
import {
  PlanOfCareItemId,
  RequiredDutySheetTask,
  SelectedDutySheetTask,
  VisitInstanceId,
} from "../messages";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { List } from "../utils/List";
import MedflytDutyTasksList from "./Timesheet/components/MedflytDutyTasksList";
import { SelectedDutyTasks } from "./Timesheet/useTimesheetForm";
import { HashMap } from "../utils/HashMap";
import { isDefined } from "./Generic/IsDefined";
import useDutySheet from "../hooks/useDutySheet";
import { getDutySheetVisitTaskKey, partitionDuties } from "../clockInOutUtils";

interface MedflytDutySheetActivityProps {
  isDutySheetCompleted: boolean;
  requiredDutyTasks: List<RequiredDutySheetTask>;
  selectedDutyTasks: SelectedDutySheetTask[];
  visitInstanceId: VisitInstanceId;
  colorRequiredTasks: boolean;
  postPatientDocAnswer: API["postPatientDocAnswer"];
  onRequestOpenErrorModal: (title: string, error: string) => void;
  onOpenChangesSavedBanner: () => void;
}

const MedflytDutySheetActivity = (props: MedflytDutySheetActivityProps) => {
  const [selectedDutyTasks, setSelectedDutyTasks] = React.useState<SelectedDutyTasks>(
    HashMap.fromArray(
      props.selectedDutyTasks.map((x: SelectedDutySheetTask) => [
        x.planOfCareItemId,
        { notes: x.notes },
      ])
    )
  );

  const partitionedDuties = React.useMemo(() => {
    return partitionDuties(props.requiredDutyTasks);
  }, [props.requiredDutyTasks]);

  const [approvedDuties, setApprovedDuties] = React.useState<
    HashMap<PlanOfCareItemId, boolean | null>
  >(
    HashMap.fromArray(
      props.selectedDutyTasks.map((x) => {
        return [
          x.planOfCareItemId,
          sessionStorage.getItem(
            getDutySheetVisitTaskKey(x.planOfCareItemId, props.visitInstanceId)
          ) !== "false",
        ];
      })
    )
  );

  const { updateDutySheetTaskNotes, updateDutySheetTask } = useDutySheet({
    postPatientDocAnswer: props.postPatientDocAnswer,
    visitInstanceId: props.visitInstanceId,
  });

  const handleCheckTask = (id: PlanOfCareItemId, isChecked: boolean) => {
    setSelectedDutyTasks((prev) => (isChecked ? prev.set(id, { notes: null }) : prev.delete(id)));
    updateDutySheetTask(id, isChecked);
  };

  const handleChangeNotes = (id: PlanOfCareItemId, notes: string | null) => {
    setSelectedDutyTasks((prev) =>
      prev.set(id, { notes: isDefined(notes) && notes !== "" ? notes : null })
    );
    updateDutySheetTaskNotes(id, notes ?? "");
  };

  const handleDutyApproval = (duty: PlanOfCareItemId, approval: boolean) => {
    approvedDuties.set(duty, approval === approvedDuties.get(duty) ? null : approval);
    setApprovedDuties(approvedDuties);
    updateDutySheetTask(duty, approvedDuties.get(duty) !== null);
  };

  const handleDisabledItemClick = () => {
    props.onRequestOpenErrorModal(
      translate(STRINGS.BEFORE_SUBMITTING_THE_DUTIES),
      translate(STRINGS.UNABLE_TO_EDIT_SUBMITTED_DUTY_SHEET)
    );
  };

  return (
    <MedflytDutyTasksList
      activeVisitId={props.visitInstanceId}
      colorRequiredTasks={props.colorRequiredTasks}
      duties={partitionedDuties}
      selectedDutyTasks={selectedDutyTasks}
      approvedDutiesMap={approvedDuties}
      onChangeNotes={handleChangeNotes}
      onCheck={handleCheckTask}
      isTaskItemDisabled={props.isDutySheetCompleted}
      onDisabledTaskItemClick={handleDisabledItemClick}
      onApproveDuty={handleDutyApproval}
    />
  );
};

export default MedflytDutySheetActivity;
